<template>
    <section class="B2BForm bg-gray-900 px-5 py-16 text-white lg:px-20 lg:py-24">
        <div class="container mx-auto grid max-w-[1100px] gap-10 lg:grid-cols-2 lg:items-center lg:gap-16">
            <div>
                <h2 class="mb-4 font-heading text-3xl font-bold leading-tight md:text-4xl">Napíšte nám</h2>
                <p class="mb-8 max-w-[44ch] text-lg leading-relaxed text-gray-300">
                    Stačí e-mail a pár slov o tom, koho hľadáte alebo s čím potrebujete pomôcť. Odpovieme vám
                    e-mailom.
                </p>
                <div class="flex items-center gap-4">
                    <div class="flex size-12 shrink-0 items-center justify-center rounded-full bg-white/10">
                        <CIcon class="size-5 text-white" name="email" />
                    </div>
                    <div>
                        <p class="!mb-0 font-semibold">Tím GROWNi</p>
                        <a class="text-gray-300 underline-offset-2 hover:text-white hover:underline" :href="`mailto:${CONTACT_EMAIL}`">
                            {{ CONTACT_EMAIL }}
                        </a>
                    </div>
                </div>
            </div>

            <div class="rounded-2xl bg-white p-6 text-gray-900 shadow-xl dark:bg-surface-card dark:text-txt-headings sm:p-8">
                <div v-if="status === 'done'" class="flex flex-col items-center py-8 text-center" role="status">
                    <div class="mb-4 flex size-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/40">
                        <CIcon class="size-6 text-emerald-600 dark:text-emerald-300" name="checkmark" />
                    </div>
                    <p class="mb-2 text-xl font-semibold">Ďakujeme, správa je odoslaná</p>
                    <p class="text-gray-600 dark:text-txt-body">Odpovieme vám na {{ sentTo }}.</p>
                </div>

                <form v-else class="flex flex-col gap-5" novalidate @submit.prevent="submit">
                    <div>
                        <label class="mb-1.5 block text-sm font-semibold" for="b2b-email">Váš e-mail</label>
                        <input
                            id="b2b-email"
                            v-model.trim="email"
                            autocomplete="email"
                            class="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-base outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-default dark:bg-surface-subtle dark:focus:ring-orange-900/40"
                            :class="{ '!border-red-500': emailError }"
                            :aria-invalid="emailError ? 'true' : 'false'"
                            aria-describedby="b2b-email-error"
                            inputmode="email"
                            placeholder="meno@firma.sk"
                            type="email"
                        />
                        <p v-if="emailError" id="b2b-email-error" class="mt-1.5 text-sm text-red-600">{{ emailError }}</p>
                    </div>

                    <div>
                        <label class="mb-1.5 block text-sm font-semibold" for="b2b-message">
                            S čím vám môžeme pomôcť?
                            <span class="font-normal text-gray-500 dark:text-txt-body">(nepovinné)</span>
                        </label>
                        <textarea
                            id="b2b-message"
                            v-model="message"
                            class="w-full resize-y rounded-xl border border-gray-300 bg-white px-4 py-3 text-base outline-none transition focus:border-orange-500 focus:ring-2 focus:ring-orange-200 dark:border-default dark:bg-surface-subtle dark:focus:ring-orange-900/40"
                            maxlength="3000"
                            placeholder="Napríklad: hľadáme marketéra na polovičný úväzok"
                            rows="4"
                        />
                    </div>

                    <!-- Honeypot: hidden from people, bots fill it in -->
                    <input v-model="website" aria-hidden="true" autocomplete="off" class="hidden" name="website" tabindex="-1" type="text" />

                    <button
                        class="flex w-full items-center justify-center gap-2 rounded-full bg-orange-500 px-6 py-3.5 text-base font-semibold text-white transition-colors hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-60"
                        :disabled="status === 'loading'"
                        type="submit"
                    >
                        {{ status === 'loading' ? 'Odosielam…' : 'Odoslať' }}
                        <span v-if="status !== 'loading'" class="text-lg">→</span>
                    </button>

                    <p v-if="status === 'error'" class="text-center text-sm text-red-600" role="alert">
                        Správu sa nepodarilo odoslať. Skúste to znova alebo nám napíšte na
                        <a class="underline" :href="`mailto:${CONTACT_EMAIL}`">{{ CONTACT_EMAIL }}</a>.
                    </p>
                </form>
            </div>
        </div>
    </section>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { LoadingState } from '#shared/types/LoadingState'
import { useB2B } from '@/composables/b2b/useB2B'

/** Shown on the pages for organizations; the form's messages go there too (B2BController). */
const CONTACT_EMAIL = 'pracuj@growni.sk'
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const { sendContactForm } = useB2B()
const route = useRoute()

const email = ref('')
const message = ref('')
const website = ref('')
const sentTo = ref('')
const status = ref<LoadingState>('idle')
const isSubmitted = ref(false)

// Only after the first submit, so the field does not turn red while someone is typing
const emailError = computed(() => {
    if (!isSubmitted.value) return ''
    if (!email.value) return 'Zadajte e-mail, aby sme vám mohli odpovedať.'
    return EMAIL_PATTERN.test(email.value) ? '' : 'Skontrolujte, prosím, e-mail.'
})

const submit = async () => {
    isSubmitted.value = true
    if (emailError.value) return

    status.value = 'loading'
    try {
        await sendContactForm({ email: email.value, description: message.value, page: route.path, website: website.value })
        sentTo.value = email.value
        status.value = 'done'
    } catch (error) {
        console.error('B2BForm', error)
        status.value = 'error'
    }
}
</script>
